/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'react',
	'@ba/ba-ui-carbon-toolkit',
	'./PropertiesContainer',
	'../../lib/@waca/carbon-icons'
], function (
	React,
	Toolkit,
	PropertiesContainer,
	CarbonIcons
) {
	const {
		Label,
		AccordionItem
	} = Toolkit;

	const {
		Reset16
	} = CarbonIcons;

	const skipSections = ['Separator'];

	// Container controls are the layout controls of type wrapper of other controls.
	// They don't have propertyId and need to have a callback to the method makeSectionItem in this class to create their wrapped controls.
	const containerControls = ['SingleLineLinks', 'Split', 'FontCombo'];

	const SUPPORTED_ICONS = {
		Reset16
	};

	const shouldSkipSpec = (spec) => {
		if (skipSections.indexOf(spec.type) > -1) {
			return true;
		}
		return false;
	};

	class V2PropertyControlsFactory {

		constructor({ controls, dashboard, paletteService, colorsService }) {
			this._dashboard = dashboard;
			this._controls = controls;
			this._paletteService = paletteService;
			this._colorsService = colorsService;
			this._logger = this._dashboard.getFeature('Logger');
			this._ref = React.createRef();
		}

		_makeBanner(banner) {
			return (
				<Label
					className="properties--title bannerLabel"
					label={banner.value}
				/>
			);
		}

		// This is for backward compatibility, new Carbon design do not have groups, only sections and its list of items 
		_collectOrderedGroups(layouts) {
			const originalGroups = layouts.filter((layout) => layout && layout.type === 'Group');
			const orderedGroups = [];
			if (originalGroups.length) {
				for (let i = 0; i < originalGroups.length; i++) {
					let group = originalGroups[i];
					if (group.items && group.items.length) {
						// return only the group that has items
						if (group.position === undefined) {
							// if there is no explicit position specified, set position to 10;
							// that means the provider doesn't care about order
							group.position = 10;
						}
						orderedGroups.push(group);
					}
				}
				orderedGroups.sort((a,b) => a.position - b.position);
			}
			return orderedGroups;
		}

		_makeNonCollapsibleSection(section) {
			const { items, label, id } = section;
			let result = [];
			result.push(
				<Label
					className={'properties--controls--SectionLabel'}
					data-section-id={id}
					label={label}
				/>
			);
			return result.concat(this._makeSectionItems(items));
		}

		_makeSectionItems(items = []) {
			let sectionItems = [];
			items.forEach((item, key) => {
				if (!shouldSkipSpec(item)) {
					if (item.type === 'Section' && item.collapsible === false) {
						// Case nested sections, section is not expandable/collapsible, generate a Label for it and convert its children
						sectionItems = sectionItems.concat(this._makeNonCollapsibleSection(item));
					} else {
						const sectionItem = this.makeSectionItem(item, key);
						if (sectionItem) {
							sectionItems.push(sectionItem);
						}
					}
				}
			});
			return sectionItems;
		}

		// collapsible sections
		_makeSection(section) {
			const { items, label, id } = section;
			const sectionItems = this._makeSectionItems(items);
			return (
				<AccordionItem
					key={id}
					className={'properties--controls--SectionLabel'}
					data-section-id={id}
					itemName={label}
					open={false}
					icon= 'right'
				>
					{sectionItems}
				</AccordionItem>
			);

		}

		_getProperty(layoutItem) {
			let property = this._metadata.find(item => item.id === layoutItem.id);
			if (property && !property.editor && layoutItem.editor && layoutItem.editor.uiControl) {
				// copy layout to the property
				property.editor = layoutItem.editor;
			}
			if (!property) {
				if (layoutItem) {
					let layoutSpec;
					if (layoutItem.editor && layoutItem.editor.uiControl) {
						layoutSpec = layoutItem.editor.uiControl;
					} else {
						layoutSpec = layoutItem;
					}
					// to make extra layout item structure consistent with property item structure
					if (layoutSpec.value) {
						layoutItem.value = layoutSpec.value;
					}
					if (layoutSpec.onChange) {
						layoutItem.setPropertyValue = layoutSpec.onChange;
					}
					property = layoutItem;
				}
			}
			return property;
		}

		_parseItemStyle(item) {
			const result = {};
			let itemStyle;
			if (item.style) {
				itemStyle = item.style;
			} else if (item.editor && item.editor.uiControl && item.editor.uiControl.style) {
				itemStyle = item.editor.uiControl.style;
			} else {
				itemStyle = '';
			}
			itemStyle
				.split(';')
				.filter(item => !!item)
				.forEach(item => {
					const [ key, value ] = item.split(':');
					result[key.trim()] = value.trim();
				});

			return result;
		}

		getIcon(iconName) {
			const iconFeature =  this._dashboard.getFeature('Icons');
			return iconFeature.getIcon(iconName);

		}

		makeSectionItem(layoutItem, key) {
			const controlType = this._getControlType(layoutItem);
			const style = this._parseItemStyle(layoutItem);
			let PropertyControl;
			let props;

			if (typeof(controlType) === 'string') {
				PropertyControl = this._controls[controlType];
				props = {
					className: `properties--controls properties--controls--${controlType}`
				};
				if (controlType === 'ColorPicker') {
					props.paletteService = this._paletteService;
					props.colorsService = this._colorsService;
				}
			} else {
				// Custom contributed control as part of the spec
				PropertyControl = controlType;
				let extraProps = {};
				if (layoutItem.props) {
					extraProps = layoutItem.props;
				} else if (layoutItem.editor && layoutItem.editor.uiControl && layoutItem.editor.uiControl.props) { // weird behaviour, we need to revisit; TODO
					extraProps = layoutItem.editor.uiControl.props;
				}
				props = {
					...extraProps,
					BUILTIN_CONTROLS: this._controls,
					className: 'properties--controls' + (extraProps.className ? ' ' + extraProps.className : '')
				};
			}
			if (PropertyControl) {
				if (containerControls.indexOf(controlType) > -1) {
					// if the layout item is of container type (has many children items)
					const makeSectionItemCb = this.makeSectionItem.bind(this);
					const getIconCb = this.getIcon.bind(this);
					return (
						<PropertyControl
							{...props}
							key={key}
							item={layoutItem}
							makeSectionItemCb={makeSectionItemCb}
							supportedIcons={SUPPORTED_ICONS}
							getIconCb={getIconCb}
							logger={this._logger}
						/>
					);
				}
				// if the layout item is the leaf item (associated with 1 property) 
				const property = this._getProperty(layoutItem);
				if (!property) {
					this._logger.warn('property not found in metadata: ',  layoutItem.id);
					return null;
				}
				if (controlType === 'ToggleButton') {
					this._handleToggleButtonRedraw_HACK(property);
				}
				return (
					<PropertyControl
						{...props}
						key={key}
						item={property}
						style={style}
					/>
				);
			} else {
				// TODO: this is the temporary solution in waiting for implementing all the control
				// components; when the implementation of all the components is done. this will no longer be necessary
				return (
					<div>
						<span>id: {layoutItem.id ? layoutItem.id : 'id'}</span>
						<span>|</span>
						<span>type: {controlType}</span>
					</div>
				);
			}
		}

		/**
		 * Get the UIControl type from its relative module path
		 * Ex: module = '../ui/uiSlider'  => return 'uiSlider'
		 * @param {String} modulePath - path of the module
		 * @return {String} module name
		 */
		_getTypeFromModule(modulePath) {
			let type = modulePath;
			if (modulePath) {
				// search the last character '/'
				const lastSlashIndex = modulePath.lastIndexOf('/');
				if (lastSlashIndex > -1 && lastSlashIndex < modulePath.length - 1) {
					type = modulePath.substring(lastSlashIndex + 1, modulePath.length);
				}
			}
			return type;
		}

		_getControlType(item) {
			let type = item.type ? item.type : null;
			// convert relative path for UiSlider to the built-in control Slider
			if (type && (typeof type === 'string') && type.endsWith('/UiSlider')) {
				type = 'UiSlider';
			}
			if (!type) {
				type = item.module && this._getTypeFromModule(item.module);
			}
			if (!type) {
				const uiControl = item.editor && item.editor.uiControl;
				if (uiControl) {
					type = this._getControlType(uiControl);
				}
			}
			if (type) {
				item.type = type;
			}
			return type;
		}

		_makeSections(layoutList) {
			const orderedGroups = this._collectOrderedGroups(layoutList);
			let sections = [];
			if (orderedGroups.length) {
				orderedGroups.forEach((group) => {
					if (group.items && group.items.length) {
						group.items.forEach(groupItem => {
							if (groupItem.type === 'Section') {
								sections.push(this._makeSection(groupItem));
							}
						});
					}
				});
			} else {
				layoutList.forEach(layout => {
					if (layout) {
						if (layout.type === 'Section') {
							if (layout.label) {
								sections.push(this._makeSection(layout));
							} else {
								// section doesn't have label, only show section items
								sections = sections.concat(this._makeSectionItems(layout.items));
							}
						} else if (layout.type !== 'Banner') {
							sections.push(this.makeSectionItem(layout, layout.id));
						}
					}
				});
			}
			return sections;
		}

		_handleToggleButtonRedraw_HACK(spec) {
			const originalSetPropertyValue = spec.setPropertyValue;
			// overrriding setPropertyValue method to perform a redraw on the main slideout contribution.
			// more details here: https://jsw.ibm.com/browse/CADBC-3471
			spec.setPropertyValue = (...args) => {
				const node = this._ref.current._node;
				const parentNode = node && node.closest('.slideout__contributions__item');
				if (parentNode && parentNode.scrollTop > 0) { // scrollbar present
					node.style.setProperty('transform', 'translateZ(0)');
					window.requestAnimationFrame(() => {
						node.style.removeProperty('transform');
					});
				}
				originalSetPropertyValue(...args);
			};
		}

		makeControls(propertyList, propertyLayoutList) {
			this._metadata = propertyList;
			const bannerProp = propertyLayoutList.find(layout => layout && layout.type === 'Banner');
			const banner = bannerProp && this._makeBanner(bannerProp);
			const sections = this._makeSections(propertyLayoutList);
			return (
				<PropertiesContainer
					useRef={this._ref}
					banner={banner}
					sections={sections}
				/>
			);
		}
	}

	return V2PropertyControlsFactory;
});